import React, { useEffect, useState } from 'react';
import { observer } from "mobx-react";
import { useContext } from 'react';
import { Container, Button, Alert, Box, Typography, Link, Grid } from '@mui/material';
import { Link as ReactRouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { styled } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
//
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
//
import { AppContext } from '/@/app/AppContext';
import { ScrollContext } from '/@/app/AppLayout';
import { Linkify } from '/@/app/linkify';
import { route } from '/@/app/routes';
import { StepInfoBlock } from '/@/app/components/StepInfoBlock';
import { AudioPlayer } from '/@/app/components/AudioPlayer';
import { userFlowState } from '/@/app/states/userFlowState';
import { SectionListState } from "/@/app/views/PlanView/SectionState.js";
import { IFrameVideo } from '/@/app/components/IFrameVideo';
import { PaywallStep, ExpireBlock } from '/@/app/components/Paywall'
import { UpdateAnnouncements } from '/@/app/components/UpdateAnnouncements';
import { useApi} from '/@/app/Api';



const Item = styled(Box)(({ theme }) => ({
    backgroundColor: '#f5f7fb',
    ...theme.typography.body2,

    borderRadius: theme.shape.borderRadius * 1.5,
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(4.5),
    },
    //
    textAlign: "left",
    color: theme.palette.text.primary,
}));


const Section = observer(({ state }) => {
    return (
        <>
            <Typography variant="h5" component="h3" gutterBottom sx={{
                color: state.completable && state.allFinished ? "green" : "inherit",
            }}>
                {state.index}. {state.name}
            </Typography>
            {state.steps.map((step, stepIndex) => (
                <Box key={stepIndex} sx={{
                    mt: 2,
                }}>
                    <Step state={step} />
                </Box>
            ))}
        </>
    )
});


const Step = observer(({ state }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    if (state.paywall_block) return (<PaywallStep data={state}/>);

    return (
        <>
            {(state.status === 'complete') ?
                <Box sx={{ color: "green", fontSize: "14px", display: "flex", flexFlow: "row nowrap", alignItems: "center", gap: 2 }}>
                    <CheckCircleIcon />
                    <Typography variant="h8" component="div" sx={{
                    }}>
                        {state.title}
                        <Link onClick={state.unMarkDone} sx={{ ml: 2 }}>
                            <ReplayIcon sx={{ fontSize: "14px", position: "relative", top: "3px" }}></ReplayIcon>
                        </Link>
                    </Typography>
                </Box>
                :
                <Box sx={{ display: "flex", flexFlow: "row nowrap", justifyContent: "left", alignItems: "center", gap: 2 }}>
                    <Typography variant="h6" component="h4" sx={{
                        color: state.status === "complete" ? "green" : "black",
                        fontWeight: "normal",
                        mt: 1,
                        mb: 2,
                    }}>
                        {state.title}
                    </Typography>
		    {state.sound_link &&
                    <AudioPlayer
                        audioSrc={state.sound_link}
                        sx={{
                            ml: 2, mr: 2, mb: 2, mt: 3,
                            justifySelf: "right",
                        }}
                        size="25px"
                        hideCaption={true}
                    />
		    }
                </Box>
            }


            {state.status !== 'complete' &&
                <>
                    {state.benefit_years>0 ?
                        <Typography variant="h6" component="h4" gutterBottom sx={{
                            color: theme.palette.warning.main,
                            fontWeight: "bold",
                            fontSize: "16px",
                            mb: 2,
                        }}>
			    {t("может добавить")}{' '}
                            {t("лет жизни", {count: state.benefit_years})} {" "}
                        </Typography>
                        : null
                    }

                    <Typography variant="body1" gutterBottom>
                        <Linkify text={state.informing_text} />
		        {state.article_link &&
                        <Link href={state.article_link} target="_blank" rel="noopener"
                              sx={{ mt: 1, display: "flex", flexFlow: "row nowrap", alignItems: "center" }}
                        >
                            <NewReleasesIcon sx={{ mr: 1 }}/> {t("Обязательно прочитайте об этом шаге")}!
                        </Link>
		        }
                    </Typography>
                    {(state.clinical_recommendation_1 || state.clinical_recommendation_2) &&
                        <Typography variant="body2">
			    {t("Ссылки на клинические рекомендации")}:{" "}
                            {state.clinical_recommendation_1 && <Link href={state.clinical_recommendation_1} target="_blank">[1]</Link>}{" "}
                            {state.clinical_recommendation_2 && <Link href={state.clinical_recommendation_2} target="_blank">[2]</Link>}{" "}
                        </Typography>
                    }
                    {(state.completable && state.status === 'new') &&
                        <Box sx={{ mt: 2 }}>
                            <Button onClick={state.markDone} variant="outlined" color="primary" size="small" sx={{ ml: 2 }}>
                                <CheckCircleOutlineIcon sx={{ mr: 1 }}/>{t("Отметить шаг выполненным")}
                            </Button>
                        </Box>
                    }
                </>
            }
        </>
    )
});

export const PlanView = () => {
    const api=useApi();
const {restoreScroll}=useContext(ScrollContext);
    const { program } = useParams();  // undefined on /plan/, program tag on /plan/:program/
    const { bootstrapData } = useContext(AppContext);
    const [plan,setPlan] =useState(null);
    const [meta,setMeta] =useState(null);

useEffect(() =>
{
	api.request('cabinet/plan',{panel_id:bootstrapData.panel.id},{api_url:'v2'})
	.then(re=>{ setMeta(re[0]); setPlan(re.slice(1)); });
}, []);

useEffect(() =>
{
	if (!plan) return;
	restoreScroll();
}, [plan]);

if (!plan) return (<></>);

    // the whole plan is always loaded; a program only hides sections on the client
    const visiblePlan = program ? plan.filter(s => s.expire_block || (s.tags || []).includes(program)) : plan;
    const sectionListState = new SectionListState(visiblePlan,api);
    return (
        <_PlanView sectionList={sectionListState} meta={meta} />
    );
}

const _PlanView = observer(({ sectionList, meta }) => {
    const { t,i18n } = useTranslation();
    const api=useApi();
    const { bootstrapData } = useContext(AppContext);
    const { featureEnabled } = useContext(AppContext);
    const help = bootstrapData.help;
    const stats = bootstrapData.stats;
    const user = bootstrapData.user;
    const panel = bootstrapData.panel;

    userFlowState.markPlanVisited();

    const resetStepStatuses = () => {
        if (confirm(t("Отметить все шаги как невыполненные?"))) {
            sectionList.resetStepStatuses();
        }
    }

    return (
        <>
            <Container maxWidth="md">
                <Box>
                    <AudioPlayer audioSrc={help.sounds['plan-'+i18n.language]} sx={{ ml: 1, mb: 1, mr: 1, float: "right" }} />
                    <Typography variant="h3" gutterBottom sx={{ padding: "10px 0" }}>{t("План")}</Typography>
                </Box>
                <Container maxWidth="sm" className="home-view-centered-content">
                    <UpdateAnnouncements mode="silent"/>
                </Container>

            </Container>

            {(userFlowState.globalPanel.last_calculated_at !== null && sectionList.allFinished) &&
                <>
                    <Container maxWidth="md">
                        <Typography variant="body1" gutterBottom>{ help.texts['plan-congratulations-'+i18n.language] } </Typography>
                    </Container>

                    {/* container for the video w/o margins */}
                    <Container maxWidth="md" sx={{ textAlign: "center", p: "0!important" }}>
                        <IFrameVideo
                            src="https://vkvideo.ru/video_ext.php?oid=-89250364&id=456239037&hash=89323ead3febd3fc"
                            placeholderContentId="intro_video_placeholder"
                            aspectRatio={1280 / 720}
                            sx={{ mt: 3, mb: 3 }}
                        />
                    </Container>

                    <Container maxWidth="md">
                        <Box>
                            <Button variant="text" onClick={ resetStepStatuses } >{t("Восстановить все шаги")}</Button>
                        </Box>
                    </Container>
                </>
            }

            <Container maxWidth="md">

            {/*

            FIXME these switches are complete mess. Needs refactoring.

            */}

            {(userFlowState.globalPanel.last_calculated_at !== null && sectionList.allUnfinished) &&
            <>

                {/* container for the video w/o margins */}
                <Container maxWidth="md" sx={{ textAlign: "center", p: "0!important" }}>
                    <IFrameVideo
                        src="https://vkvideo.ru/video_ext.php?oid=-89250364&id=456239043&hash=79df6717f394602b"
                        placeholderContentId="intro_video_placeholder"
                        aspectRatio={1280 / 720}
                        sx={{ mt: 3, mb: 3 }}
                    />
                </Container>

            </>
            }
            {(userFlowState.globalPanel.last_calculated_at !== null && sectionList.someFinished) &&
            <>

                {/* container for the video w/o margins */}
                <Container maxWidth="md" sx={{ textAlign: "center", p: "0!important" }}>
                    <IFrameVideo
                        src="https://vkvideo.ru/video_ext.php?oid=-89250364&id=456239041&hash=03be8ad6a08e4f0d"
                        placeholderContentId="intro_video_placeholder"
                        aspectRatio={1280 / 720}
                        sx={{ mt: 3, mb: 3 }}
                    />
                </Container>

            </>
            }
            {userFlowState.globalPanel.last_calculated_at !== null &&
                <Box sx={{ mb: 3, display: "flex", flexFlow: "row wrap", gap: "10px" }}>
		    <Button variant="outlined" size="small" onClick={(e) => api.download(`/cabinet/exports/plan/${panel.id}/pdf`,{},{api_url:'v2', disable:e.currentTarget})} >{t("Скачать план в PDF")}</Button>
		    <Button variant="outlined" size="small" onClick={(e) => api.download(`/cabinet/exports/plan/${panel.id}/html`,{disposition:'inline'},{target:'_blank',api_url:'v2', disable:e.currentTarget})} >{t("Открыть экспорт")}</Button>
		    <Button variant="outlined" size="small" onClick={(e) => api.download(`/cabinet/exports/plan/${panel.id}/pdf`,{mode:'full'},{api_url:'v2', disable:e.currentTarget})} >{t("Скачать полный экспорт в PDF")}</Button>
                </Box>
            }

            {(!sectionList.allFinished && userFlowState.globalPanel.last_calculated_at !== null) &&
                <>
                    {sectionList.sections.length === 0 ?
                        <Alert severity="warning">
                            {(stats.questions_answered > 0 && stats.questions_all !== stats.questions_answered) ?
                                <Box sx={{ fontSize: "24px", mb: 3 }}>
				    {t("Вы прошли")} {stats.questions_answered} {t("из")} {stats.questions_all} {t("вопросов")}.
                                    <br />
                                    <Box sx={{ display: "flex", flexFlow: "row wrap", alignItems:"center", gap: "15px", mt: 2 }}>
                                        <Button component={ReactRouterLink} to={route("/questions/")} variant="contained" color="primary" size="small">{t("Пройти оставшиеся вопросы")}</Button>
                                        <Box sx={{fontSize: "18px" }} component="span">{t("чтобы получить план")}.</Box>
                                    </Box>
                                </Box>
                                :
                                <Box>
				    {t("Чтобы Nestarenie.Expert смог составить для вас план")}{", "}
                                    <ReactRouterLink to={route("/questions/")}>{t("пройдите все вопросы")}</ReactRouterLink>{" "}
                                    <br />
                                    <br />
				    {t("Заполните")} <ReactRouterLink to={route("/lab-tests/")}>{t("данные анализов")}</ReactRouterLink>.
				    {t("Не обязательно заполнять всё, если вы не сдавали кровь в лаборатории. Можете заполнить только те поля, которые знаете. Например,")} <b>{t("вес")}</b>, <b>{t("рост")}</b>{t(" и ")}<b>{t("окружность талии")}</b> — {t("это сделает результаты более точными")}.
                                </Box>
                            }
                        </Alert>
                        :
                        <>
                            {sectionList.anyUnfinished &&
                                <Box>
                                    <Button
                                        variant="text"
                                        onClick={ resetStepStatuses }
                                    >{t("Восстановить все шаги")}</Button>
                                </Box>
                            }
                            <Grid container spacing={2}>
                                {sectionList.sections.map((section) => (
				    section.expire_block ?  (<ExpireBlock data={section}/>) :(
                                    <Grid item xs={12} key={section.index}>
                                        <Item>
                                            <Section state={section} />
                                        </Item>
                                    </Grid>)
                                ))}
                            </Grid>
                        </>
                    }

                </>
            }
                <StepInfoBlock text={ help.texts['plan-footer-'+i18n.language] } />
            </Container>
        </>
    );
});
